import { Injectable } from '@angular/core';
import { UserEngagementModel } from 'app/services/models/engagement/user-engagement-model';
import { ServiceBase } from 'app/services/servicebase';
import { HttpService } from 'app/services/http.service';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from 'app/services/auth.service';
import { DomainService } from 'app/services/domain.service';
import { HttpParams } from '@angular/common/http';
import { Response } from "services/models/response";

@Injectable({
  providedIn: 'root'
})
export class UserEngagementService extends ServiceBase<UserEngagementModel> {
  constructor(http: HttpService, private router: Router, private authService: AuthService, private domainService: DomainService
  ) {
    super('webaccesslog', http);
  }

  createEntity(data: any): UserEngagementModel {
    return UserEngagementModel.create(data);
  }

  getTopUsers(startDate: string, endDate: string, queryParams: any): Observable<Response<UserEngagementModel[]>> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate)
      .set('$top', queryParams.top.toString())
      .set('$skip', queryParams.skip.toString())
      .set('$orderby', `${queryParams.orderBy} ${queryParams.orderByOrder}`);

    return this.http.get(`${this.endpoint}/top-users`, { observe: 'response', params }).pipe(
      map(res => {
        const response = new Response<UserEngagementModel[]>();
        response.data = (res.body ?? []).map(item => this.createEntity(item));
        response.count = Number(res.headers.get('x-count') || 0);
        return response;
      })
    );
  }

  getTopScreens(startDate: string, endDate: string): Observable<UserEngagementModel[]> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get(`${this.endpoint}/top-screens`, { params }).pipe(
      map((response: any) => {
        const body = response.body ?? response;
        if (!Array.isArray(body)) return [];

        return body.map(item => this.createEntity({
          screen: item.screenName,
          amountAccess: item.amountAccess
        }));
      })
    );
  }
  sendAccessLog(log: UserEngagementModel): Observable<any> {
    return this.http.post(`${this.endpoint}`, log);
  }

  initializeTracking(): void {
    const ignoredRoutes = [
      '/home',
      '/login',
      '/error',
      '/user-engagement',
      '/clock',
      '/frmadm',
      '/workwithus',
      '/relatos',
      '/research/form/answer'
    ];

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        const cleanUrl = this.normalizeUrl(event.urlAfterRedirects);

        if (this.shouldIgnoreRoute(cleanUrl, ignoredRoutes)) {
          return;
        }

        const domain = this.domainService.getDomain();

        const log: UserEngagementModel = new UserEngagementModel();
        log.userName = this.authService.GetName() ?? '';
        log.domainName = domain?.domainName ?? '';
        log.companyGroupId = domain?.id ?? '';
        log.screen = this.extractScreenName(cleanUrl);
        log.dateTime = new Date().toISOString();
        log.browser = this.getBrowserInfo();
        log.os = this.getOSInfo();
        log.url = cleanUrl;

        this.sendAccessLog(log).subscribe({
          next: () => { },
          error: err => console.error('Erro ao registrar acesso:', err)
        });
      });
  }

  private normalizeUrl(url: string): string {
    return url.toLowerCase().split('?')[0];
  }

  private shouldIgnoreRoute(url: string, ignoredRoutes: string[]): boolean {
    const publicRouteSegments = [
      '/frmadm',
      '/relatos',
      '/workwithus',
      '/research/form/answer',
      '/hiring/recruitment/tracking',
    ];

    const isPublicRoute = publicRouteSegments.some(segment => url.includes(segment));
    const isIgnoredRoute = ignoredRoutes.some(route => url === route || url.endsWith(route));

    return isPublicRoute || isIgnoredRoute;
  }

  private extractScreenName(url: string): string {
    return url.split('/').filter(Boolean).join(' > ');
  }

  private getBrowserInfo(): string {
    const userAgent = navigator.userAgent.toLowerCase();

    const browsers = [
      { name: 'Edge', regex: /edg/ },
      { name: 'Opera', regex: /opr/ },
      { name: 'Chrome', regex: /chrome|chromium|crios/ },
      { name: 'Firefox', regex: /firefox|fxios/ },
      { name: 'Safari', regex: /safari/ }
    ];

    const match = browsers.find(browser => browser.regex.test(userAgent));
    return match ? match.name : 'Desconhecido';
  }

  private getOSInfo(): string {
    const platform = navigator.platform.toLowerCase();

    const systems = [
      { name: 'Windows', regex: /win/ },
      { name: 'MacOS', regex: /mac/ },
      { name: 'Linux', regex: /linux/ },
      { name: 'Android', regex: /android/ },
      { name: 'iOS', regex: /iphone|ipad/ }
    ];

    const match = systems.find(system => system.regex.test(platform));
    return match ? match.name : 'Desconhecido';
  }

}
